import { Enum } from '../../../common/constants'
import { StatementSummary } from '../../../domain/invoicing/statement'

export const StatementSelectionModes = {
  Specific: 'specific',
  All: 'all',
} as const

export type StatementSelectionMode = Enum<typeof StatementSelectionModes>

export type SelectedStatementMeta = {
  total: number
  isExported: boolean
}

export type SpecificStatementSelection = {
  mode: typeof StatementSelectionModes.Specific
  picked: Map<string, SelectedStatementMeta>
}

export type AllStatementSelection = {
  mode: typeof StatementSelectionModes.All
  allIds: string[]
  excluded: Map<string, SelectedStatementMeta>
}

export type StatementSelection = SpecificStatementSelection | AllStatementSelection

export const emptyStatementSelection = (): StatementSelection => ({ mode: StatementSelectionModes.Specific, picked: new Map() })

const metaFromStatement = (statement: StatementSummary): SelectedStatementMeta => ({ total: statement.total, isExported: statement.qboSyncState !== 'not-synced' })

export const isStatementSelected = (selection: StatementSelection, statementId: string): boolean => {
  if (selection.mode === StatementSelectionModes.Specific) {
    return selection.picked.has(statementId)
  }
  return !selection.excluded.has(statementId)
}

export const toggleStatement = (selection: StatementSelection, statement: StatementSummary): StatementSelection => {
  if (selection.mode === StatementSelectionModes.Specific) {
    const picked = new Map(selection.picked)
    if (picked.has(statement.statementId)) {
      picked.delete(statement.statementId)
    } else {
      picked.set(statement.statementId, metaFromStatement(statement))
    }
    return { mode: StatementSelectionModes.Specific, picked }
  }
  const excluded = new Map(selection.excluded)
  if (excluded.has(statement.statementId)) {
    excluded.delete(statement.statementId)
  } else {
    excluded.set(statement.statementId, metaFromStatement(statement))
  }
  return { mode: StatementSelectionModes.All, allIds: selection.allIds, excluded }
}

export const selectAllStatements = (allIds: string[]): StatementSelection => ({
  mode: StatementSelectionModes.All,
  allIds,
  excluded: new Map(),
})

export const selectedStatementIds = (selection: StatementSelection): string[] => {
  if (selection.mode === StatementSelectionModes.Specific) {
    return Array.from(selection.picked.keys())
  }
  return selection.allIds.filter(id => !selection.excluded.has(id))
}

export const selectedStatementCount = (selection: StatementSelection): number => {
  if (selection.mode === StatementSelectionModes.Specific) {
    return selection.picked.size
  }
  return selection.allIds.length - selection.excluded.size
}

// Specific mode only — we cache each picked statement's total. In 'all' mode we have
// no per-statement totals (Select-All gives us ids only), so the consumer derives the
// total from page metadata minus excludedStatementTotalCents instead.
export const selectedStatementTotalCents = (selection: StatementSelection): number => {
  if (selection.mode !== StatementSelectionModes.Specific) {
    return 0
  }
  let sum = 0
  selection.picked.forEach(meta => { sum += meta.total })
  return sum
}

export const excludedStatementTotalCents = (selection: StatementSelection): number => {
  if (selection.mode !== StatementSelectionModes.All) {
    return 0
  }
  let sum = 0
  selection.excluded.forEach(meta => { sum += meta.total })
  return sum
}

export const isAnyStatementExported = (selection: StatementSelection, loadedStatements: StatementSummary[]): boolean => {
  if (selection.mode === StatementSelectionModes.Specific) {
    for (const meta of selection.picked.values()) {
      if (meta.isExported) {
        return true
      }
    }
    return false
  }
  return loadedStatements.some(x => !selection.excluded.has(x.statementId) && x.qboSyncState !== 'not-synced')
}
